/**
 * Pricing Breakdown Component
 * Shows detailed pricing breakdown for the custom safari
 */

import React, { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { SafariPricing } from '../../types/customSafari';
import { useTheme } from '../../contexts/ThemeContext';
import { currencyService } from '../../services/currencyService';

interface PricingBreakdownProps {
  pricing: SafariPricing;
  selectedCurrency?: string;
  onCurrencyChange?: (currency: string) => void;
}

const PricingBreakdown: React.FC<PricingBreakdownProps> = ({ 
  pricing, 
  selectedCurrency,
  onCurrencyChange 
}) => {
  const { actualTheme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [currencies, setCurrencies] = useState<string[]>([]);

  useEffect(() => {
    const loadCurrencies = async () => {
      const list = await currencyService.getSupportedCurrencies();
      setCurrencies(list);
    };
    loadCurrencies();
  }, []);
  const displayCurrency = selectedCurrency || pricing.currency;

  const formatCurrency = (amount: number, currency?: string) => {
    const curr = currency || displayCurrency;
    return currencyService.formatCurrency(amount, curr);
  };

  return (
    <div className={`rounded-lg ${
      actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white border border-neutral-200'
    } overflow-hidden`}>
      {/* Summary Header */}
      <div className={`p-6 ${
        actualTheme === 'dark' ? 'bg-slate-800' : 'bg-white'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${
            actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
          }`}>
            Pricing Breakdown
          </h3>
          {/* Currency Selector */}
          {onCurrencyChange && (
            <div className="flex items-center gap-2">
              <label className={`text-sm ${
                actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
              }`}>
                Currency:
              </label>
              <select
                value={displayCurrency}
                onChange={(e) => onCurrencyChange(e.target.value)}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                  actualTheme === 'dark'
                    ? 'bg-slate-700 border-slate-600 text-white'
                    : 'bg-white border-neutral-300 text-neutral-900'
                } focus:outline-none focus:ring-2 focus:ring-primary-500`}
              >
                {currencies.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className={`text-2xl font-bold ${
          actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
        }`}>
          {formatCurrency(pricing.total, displayCurrency)}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`mt-2 text-sm ${
            actualTheme === 'dark' ? 'text-slate-400 hover:text-slate-300' : 'text-neutral-600 hover:text-neutral-800'
          } transition-colors flex items-center gap-1`}
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon className="h-4 w-4" />
              Hide Details
            </>
          ) : (
            <>
              <ChevronDownIcon className="h-4 w-4" />
              Show Details
            </>
          )}
        </button>
      </div>

      {/* Detailed Breakdown */}
      {isExpanded && (
        <div className={`px-6 pb-6 space-y-3 border-t ${
          actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
        }`}>
          {/* Tours Subtotal */}
          <div className="flex justify-between items-center pt-4">
            <span className={`${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
            }`}>
              Tours Subtotal
            </span>
            <span className={`font-semibold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              {formatCurrency(pricing.toursSubtotal, displayCurrency)}
            </span>
          </div>

          {/* Transfers Subtotal */}
          {pricing.transfersSubtotal > 0 && (
            <div className="flex justify-between items-center">
              <span className={`${
                actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
              }`}>
                Transfers Subtotal
              </span>
              <span className={`font-semibold ${
                actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
              }`}>
                {formatCurrency(pricing.transfersSubtotal, displayCurrency)}
              </span>
            </div>
          )}

          {/* Extras Subtotal */}
          {pricing.extrasSubtotal > 0 && (
            <div className="flex justify-between items-center">
              <span className={`${
                actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
              }`}>
                Extras Subtotal
              </span>
              <span className={`font-semibold ${
                actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
              }`}>
                {formatCurrency(pricing.extrasSubtotal, displayCurrency)}
              </span>
            </div>
          )}

          {/* Subtotal */}
          <div className={`flex justify-between items-center pt-2 border-t ${
            actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
          }`}>
            <span className={`font-medium ${
              actualTheme === 'dark' ? 'text-slate-200' : 'text-neutral-800'
            }`}>
              Subtotal
            </span>
            <span className={`font-semibold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              {formatCurrency(pricing.subtotal, displayCurrency)}
            </span>
          </div>

          {/* Taxes */}
          <div className="flex justify-between items-center">
            <span className={`${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
            }`}>
              VAT (16%)
            </span>
            <span className={`font-semibold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              {formatCurrency(pricing.taxes, displayCurrency)}
            </span>
          </div>

          {/* Service Fee */}
          <div className="flex justify-between items-center">
            <span className={`${
              actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
            }`}>
              Service Fee
            </span>
            <span className={`font-semibold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              {formatCurrency(pricing.fees, displayCurrency)}
            </span>
          </div>

          {/* Discount */}
          {pricing.discount && pricing.discount > 0 && (
            <div className="flex justify-between items-center text-green-600">
              <span>Discount</span>
              <span className="font-semibold">
                -{formatCurrency(pricing.discount, displayCurrency)}
              </span>
            </div>
          )}

          {/* Total */}
          <div className={`flex justify-between items-center pt-3 border-t-2 ${
            actualTheme === 'dark' ? 'border-slate-600' : 'border-primary-200'
          }`}>
            <span className={`text-lg font-bold ${
              actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
            }`}>
              Total
            </span>
            <span className={`text-xl font-bold ${
              actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-800'
            }`}>
              {formatCurrency(pricing.total, displayCurrency)}
            </span>
          </div>

          {/* Detailed Breakdown */}
          {pricing.breakdown.length > 0 && (
            <div className={`mt-4 pt-4 border-t ${
              actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
            }`}>
              <h4 className={`text-sm font-semibold mb-3 ${
                actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
              }`}>
                Itemized Breakdown
              </h4>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {pricing.breakdown.map((item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between items-start text-sm ${
                      actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{item.item}</div>
                      {item.description && (
                        <div className="text-xs opacity-75">{item.description}</div>
                      )}
                    </div>
                    <div className="ml-4 text-right">
                      <div className="font-semibold">{formatCurrency(item.total, displayCurrency)}</div>
                      {item.quantity > 1 && (
                        <div className="text-xs opacity-75">
                          {item.quantity} × {formatCurrency(item.unitPrice, displayCurrency)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PricingBreakdown;

